import { FC } from 'react';
import classnames from 'classnames';
import { format, eachYearOfInterval, isSameYear, startOfYear, endOfYear } from 'date-fns';

import { useDisabled } from '@hh.ru/magritte-common-use-disabled';
import { YearPickerProps, IntervalYearPickerProps } from '@hh.ru/magritte-ui-date-picker/types';
import { useIntervalPicker } from '@hh.ru/magritte-ui-date-picker/useIntervalPicker';
import { makeYearsInterval } from '@hh.ru/magritte-ui-date-picker/utils';

import styles from './date-picker.less';

const YEARS_IN_ROW = 3;

export const YearPicker: FC<YearPickerProps> = ({ displayDate, selectedDate, onDateSelect }) => {
    const dates = eachYearOfInterval(makeYearsInterval(displayDate));
    const forceDisabled = useDisabled();

    return (
        <div className={classnames(styles.calendar, styles.calendarYears)} data-qa="date-picker-years">
            {dates.map((date) => {
                const isSelected = !!selectedDate && isSameYear(date, selectedDate);

                return (
                    <div className={styles.item} key={`${date}`}>
                        <button
                            className={classnames(styles.text, {
                                [styles.textDisabled]: forceDisabled,
                                [styles.textSelected]: isSelected,
                                [styles.textToday]: isSameYear(date, Date.now()),
                                [styles.textUnselected]: !forceDisabled && !isSelected,
                            })}
                            onClick={() => onDateSelect?.(date)}
                        >
                            {format(date, 'yyyy')}
                        </button>
                    </div>
                );
            })}
        </div>
    );
};

export const IntervalYearPicker: FC<IntervalYearPickerProps> = ({
    displayDate,
    selectionStart,
    selectionEnd,
    onStartSelect,
    onEndSelect,
}) => {
    const dates = eachYearOfInterval(makeYearsInterval(displayDate));

    const datesProps = useIntervalPicker({
        dates,
        selectionStart: selectionStart && startOfYear(selectionStart),
        selectionEnd: selectionEnd && endOfYear(selectionEnd),
        onStartSelect,
        onEndSelect,
        isEqual: isSameYear,
    });

    return (
        <div className={classnames(styles.calendar, styles.calendarYears)} data-qa="date-picker-years">
            {eachYearOfInterval(makeYearsInterval(displayDate)).map((date, index) => {
                const { isDisabled, isIntervalStart, isIntervalEnd, isSelected, isWithinInterval, mouseHandlers } =
                    datesProps[index];
                const isIntervalRowStart = isWithinInterval && !isIntervalStart && index % YEARS_IN_ROW === 0;
                const isIntervalRowEnd =
                    isWithinInterval && !isIntervalEnd && index % YEARS_IN_ROW === YEARS_IN_ROW - 1;

                return (
                    <div
                        className={classnames(styles.item, {
                            [styles.itemInterval]: isWithinInterval,
                            [styles.itemIntervalStart]: isIntervalStart,
                            [styles.itemIntervalEnd]: isIntervalEnd,
                            [styles.itemIntervalRowStart]: isIntervalRowStart,
                            [styles.itemIntervalRowEnd]: isIntervalRowEnd,
                        })}
                        key={`${date}`}
                    >
                        <button
                            {...mouseHandlers}
                            className={classnames(styles.text, {
                                [styles.textDisabled]: isDisabled,
                                [styles.textSelected]: isSelected,
                                [styles.textToday]: isSameYear(date, Date.now()),
                                [styles.textUnselected]: !isDisabled && !isSelected,
                            })}
                        >
                            {format(date, 'yyyy')}
                        </button>
                        {isWithinInterval && <div className={styles.substrate} />}
                    </div>
                );
            })}
        </div>
    );
};
